import type { MouseEvent } from "react";

import { useTranslations } from "@ui/providers/useTranslations";
import { secondsToMs } from "@ui/utils/secondsToMs/secondsToMs";
import classNames from "classnames";

import { Button } from "@jti/core-components/components/Button";

import { shouldAnnounceResendCountdown } from "../../utils/shouldAnnounceResendCountdown";

import styles from "./ResendCodeSection.module.scss";

export interface ResendCodeSectionProps {
  isCounterTicking: boolean;
  remainingTime: number;
  isDisabled: boolean;
  resendBtnNextLine: boolean;
  onResend: (event: MouseEvent<HTMLButtonElement>) => void;
}

export const ResendCodeSection = ({
  isCounterTicking,
  remainingTime,
  isDisabled,
  resendBtnNextLine,
  onResend,
}: ResendCodeSectionProps) => {
  const { __ } = useTranslations();
  const resendTimerText = __("auth.registration.login_credentials.email_verify.resend_text.request_another");
  const formattedRemainingTime = secondsToMs(remainingTime);
  const liveRegionAnnouncement = shouldAnnounceResendCountdown(remainingTime)
    ? `${resendTimerText} ${formattedRemainingTime}`
    : "";

  if (isCounterTicking) {
    return (
      <div
        role="status"
        aria-live="polite"
        className={classNames(styles.text, styles.row)}
        data-testid="resendCode-timer"
      >
        <span aria-hidden="true">
          {resendTimerText}
          <span>{formattedRemainingTime}</span>
        </span>
        <span className={styles.visuallyHidden} data-testid="resendCode-timer-live-region">
          {liveRegionAnnouncement}
        </span>
      </div>
    );
  }

  return (
    <div className={styles.text}>
      {__("profile.phone_update_page.resend_text")}
      <Button
        disabled={isDisabled}
        className={classNames(styles.resendBtn, { [styles.resendBtnNextLine]: resendBtnNextLine })}
        data-testid="resendCode"
        variant="text"
        onClick={onResend}
      >
        {__("profile.phone_update_page.resend_button")}
      </Button>
    </div>
  );
};
